import { Component, ElementRef, HostBinding, Input, OnChanges, OnDestroy, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { RakSmeServService } from 'src/raksme/services/raksme-services/rakservices.service';
import { EventsService } from 'src/raksme/services/events/events.service';
import { EncryptionService } from 'src/raksme/services/encryption/encryption.service';

/**
 * Page Sections authored in Page Management (`content.sections[]` from the pages
 * API), rendered with the block markup each `layout` names.
 *
 * The markup of every block was moved here unchanged from the English About Us
 * page, which was the only page rendering layouts. Keeping it in one place lets
 * any page - English or Arabic - render the same blocks.
 *
 * ViewEncapsulation.None: the blocks rely on the theme's global classes and on
 * the page-only rules now in assets/css/raksme-sections.css (scoped under
 * .raksme-page-sections), exactly as the originals did.
 *
 * Arabic: each text field reads `<field>_ar`, falling back to the English value
 * when the Arabic one is empty, so a missing translation never blanks a block.
 */
@Component({
  selector: 'raksme-page-sections',
  templateUrl: './section.component.html',
  encapsulation: ViewEncapsulation.None
})
export class PageSectionsComponent implements OnChanges, OnDestroy {

  /** The wrapper class raksme-sections.css scopes its rules under. */
  @HostBinding('class.raksme-page-sections') readonly hostClass = true;

  /** Arabic: two-column blocks mirror (raksme-sections.css), whatever html[dir] says. */
  @HostBinding('class.raksme-page-sections--ar') get isAr(): boolean { return this.lang === 'ar'; }

  /** The raw `content.sections` array from the pages API. */
  @Input() sections: any[] = [];

  @Input() lang: 'en' | 'ar' = 'en';

  /** The API's `body_img_path`, prefixed to every image filename. */
  @Input() bodyImgPath: string = '';

  public items: any[] = [];
  public langUrl: string = '';

  /*
   * Lists for the Services and Events layouts, fetched the way the homepage's
   * home-services and home-events components fetch them - and only when a section
   * uses one of those layouts.
   */
  public serviceDatas: any = [];
  public servicesFailed: boolean = false;
  public servicesLoaded: boolean = false;
  public eventItems: any[] = [];
  public eventsFailed: boolean = false;
  public eventsLoaded: boolean = false;

  private subscriptions: Subscription[] = [];
  private themeTimer: any = null;
  private observer: any = null;

  constructor(private sanitizer: DomSanitizer, private rakService: RakSmeServService,
              private eventService: EventsService, private encrypt: EncryptionService,
              private route: Router, private host: ElementRef) { }

  ngOnChanges(changes: SimpleChanges): void {
    this.langUrl = this.lang === 'ar' ? '/ar' : '';
    this.items = this.map(this.sections);

    this.unsubscribe();
    if (this.items.some((s: any) => s.layout === 'services')) { this.getServiceList(); }
    if (this.items.some((s: any) => s.layout === 'events')) { this.getEventList(); }

    this.initTheme();
  }

  ngOnDestroy(): void {
    this.unsubscribe();
    if (this.themeTimer) { clearTimeout(this.themeTimer); }
    if (this.observer) { this.observer.disconnect(); this.observer = null; }
  }

  private unsubscribe(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions = [];
  }

  /**
   * Whether a field has something to show. The rich-text editor leaves markup such
   * as <p><br></p> behind when a field is cleared, so tags and non-breaking spaces
   * don't count as content.
   */
  private filled(v: any): boolean {
    if (v === undefined || v === null) { return false; }
    return String(v).replace(/<[^>]*>/g, '').replace(/&nbsp;|\u00a0/g, ' ').trim() !== '';
  }

  /** `<key>_ar` for Arabic when it has content, otherwise the English value; '' when neither has. */
  private pick(x: any, key: string): any {
    if (this.lang === 'ar' && this.filled(x[key + '_ar'])) { return x[key + '_ar']; }
    return this.filled(x[key]) ? x[key] : '';
  }

  /** Stored as '1' by the admin; tolerate a boolean or number too. */
  private flag(v: any): boolean {
    return v === true || String(v) === '1';
  }

  /** The trust call the `safe: 'html'` pipe made in the template, made once here. */
  private trust(raw: any): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(raw === undefined || raw === null ? '' : String(raw));
  }

  private map(raw: any): any[] {
    if (!Array.isArray(raw)) { return []; }
    const imgBase: string = this.bodyImgPath || '';
    return raw
      .filter((x: any) => x && String(x.active) !== '0')
      .map((x: any) => {
        // A section holds a list of images; older ones carry a single `image`.
        // Uploaded filenames carry spaces and parentheses, hence encodeURI - and
        // the quoted url("...") below, since encodeURI leaves parentheses as-is.
        const images: string[] = (Array.isArray(x.images) && x.images.length ? x.images : (x.image ? [x.image] : []))
          .filter((i: any) => !!i)
          .map((i: string) => imgBase + encodeURI(i));
        const title = this.pick(x, 'title');
        const body = this.pick(x, 'body');
        const subtitle = this.pick(x, 'subtitle');
        const btnLabel = this.pick(x, 'btn_label');
        const btnUrl = this.filled(x.btn_url) ? String(x.btn_url).trim() : '';
        return {
          layout: x.layout || '',
          // Every block may carry a title, subtitle and description (body); an
          // empty one renders nothing.
          title: title,
          titleHtml: this.trust(title),
          subtitle: subtitle,
          body: body,
          bodyHtml: this.trust(body),
          // Full-width blocks show their heading only when one of its parts is set.
          hasHeading: !!(title || subtitle),
          images: images,
          image: images[0] || '',
          bg: images[0] ? 'url("' + images[0] + '")' : '',
          btn_label: btnLabel,
          btnLabelHtml: this.trust(btnLabel),
          btn_url: btnUrl,
          btn_new_window: this.flag(x.btn_new_window),
          // A button renders only with both a label and a link.
          hasButton: !!(btnLabel && btnUrl),
          btn2_label: this.pick(x, 'btn2_label'),
          btn2_form: x.btn2_form,
          btn2_new_window: this.flag(x.btn2_new_window),
          name: this.pick(x, 'name'),
          designation: this.pick(x, 'designation'),
          // |-separated, the same format as the original chairman_keypoint.
          keypoints: String(this.pick(x, 'keypoints') || '').split('|').map((k: string) => k.trim()).filter((k: string) => !!k),
          // One "number|label" per line. options is built exactly as the original
          // counter1..4 are, so the theme animates them the same way.
          counters: String(this.pick(x, 'counters') || '').split('\n').filter((l: string) => l.trim() !== '').map((l: string) => {
            const cut = l.indexOf('|');
            const number = (cut === -1 ? l : l.slice(0, cut)).trim();
            return {
              number: number,
              text: cut === -1 ? '' : l.slice(cut + 1).trim(),
              options: '{"targetNumber":"' + number + '"}'
            };
          })
        };
      });
  }

  /**
   * A Service block's Apply button, as Our Services' getEncryptionD(): the form id
   * is encrypted into /our-services/<service>/form/<id> (plus /ar on Arabic).
   * btn_url already holds our-services/<service>.
   */
  applyForm(sec: any): void {
    const url = '/' + sec.btn_url + '/form/' + this.encrypt.encryptData('encrypt', sec.btn2_form) + this.langUrl;
    if (!sec.btn2_new_window) {
      this.route.navigate([url]);
    } else {
      window.open(this.route.createUrlTree([url]).toString(), '_blank');
    }
  }

  getServiceList(): void {
    this.servicesFailed = false;
    this.servicesLoaded = false;
    this.subscriptions.push(
      this.rakService.getServices('services/' + this.lang, { "url": 'all' }).subscribe(
        res => { this.serviceDatas = res; this.servicesLoaded = true; this.initTheme(); },
        () => { this.servicesFailed = true; this.servicesLoaded = true; }
      )
    );
  }

  getEventList(): void {
    this.eventsFailed = false;
    this.eventsLoaded = false;
    this.eventItems = [];
    this.subscriptions.push(
      this.eventService.getAllEvents('events/' + this.lang, { "category": 0 }).subscribe(
        (res: any) => {
          // The homepage shows the first three.
          for (const x in res.data) {
            this.eventItems.push(res.data[x]);
            if (this.eventItems.length === 3) { break; }
          }
          this.eventsLoaded = true;
          this.initTheme();
        },
        () => { this.eventsFailed = true; this.eventsLoaded = true; }
      )
    );
  }

  /**
   * theme.min.js wires up data-* hooks once, when the footer injects it - before
   * sections arrive from the API - so the blocks' parallax, counters, carousel,
   * fit-text and responsive backgrounds never started. Re-run the theme's
   * initialisers once Angular has rendered, in the order its own ajax load-more
   * uses (onSuccess), limited to elements inside this component.
   *
   * Every theme plugin stores itself with $.data(el, 'plugin_<name>') and skips
   * elements that already have one, so calling again after the services/events
   * lists load (or after the theme script re-runs on navigation) initialises only
   * the new elements, and nothing outside this component is touched.
   */
  private initTheme(): void {
    if (typeof window === 'undefined') { return; }
    if (this.themeTimer) { clearTimeout(this.themeTimer); }
    this.themeTimer = setTimeout(() => {
      this.themeTimer = null;
      const $: any = (window as any).jQuery;
      if (typeof $ !== 'function' || !$.fn) { return; }
      const root = this.host.nativeElement;
      const has = (name: string) => typeof $.fn[name] === 'function';

      if (has('liquidResponsiveBG')) {
        $('[data-responsive-bg=true]', root)
          .filter((i: number, el: any) => !$(el).parent('[data-reveal]').length)
          .liquidResponsiveBG();
      }
      if (has('liquidSplitText')) {
        $('[data-split-text]', root)
          .filter((i: number, el: any) => !$(el).parents('[data-custom-animations]').length && !el.hasAttribute('data-custom-animations'))
          .liquidSplitText();
      }
      if (has('liquidFitText')) { $('[data-fittext]', root).liquidFitText(); }
      if (has('liquidCustomAnimations')) {
        $('[data-custom-animations]', root)
          .filter((i: number, el: any) => !$(el).closest('.vc_row[data-row-bg]').length && !$(el).closest('.vc_row[data-slideshow-bg]').length)
          .liquidCustomAnimations();
      }
      // Only once its cells exist: flickity initialised on an empty list would be
      // marked as done and never pick up the services that load afterwards.
      if (has('liquidCarousel')) {
        $('[data-lqd-flickity]', root)
          .filter((i: number, el: any) => $(el).children('.carousel-item').length > 0)
          .liquidCarousel();
      }
      if (has('liquidParallax')) { $('[data-parallax]', root).not('[data-pin]:not(.vc_row)').liquidParallax(); }
      if (has('liquidInView')) { $('[data-inview]', root).liquidInView(); }
      if (has('liquidCounter')) { $('[data-enable-counter]', root).liquidCounter(); }
    });
    setTimeout(() => { this.observeSections(); this.guardImages(); });
  }

  /** Shown in place of an image whose file is missing (raksme-sections.css). */
  private static readonly MISSING_IMAGE = '/assets/img/logo/logo.png';

  /**
   * An image set on a block whose file is missing shows the RAK SME logo on a light
   * panel instead of a broken-image icon - both <img> tags and the background images
   * of the Feature and Service columns. An image that was never set is not rendered
   * at all (the template's *ngIf).
   */
  private guardImages(): void {
    if (typeof window === 'undefined') { return; }
    const root: HTMLElement = this.host.nativeElement;
    const missing = PageSectionsComponent.MISSING_IMAGE;

    Array.prototype.slice.call(root.querySelectorAll('img')).forEach((img: HTMLImageElement) => {
      if ((img as any).__raksmeGuard) { return; }
      (img as any).__raksmeGuard = true;
      const swap = () => {
        if (img.classList.contains('raksme-img-missing')) { return; }
        img.classList.add('raksme-img-missing');
        img.setAttribute('src', missing);
      };
      img.addEventListener('error', swap);
      if (img.complete && img.getAttribute('src') && img.naturalWidth === 0) { swap(); }
    });

    Array.prototype.slice.call(root.querySelectorAll('[style*="background-image"]')).forEach((el: HTMLElement) => {
      const m = /url\(["']?(.*?)["']?\)/.exec(el.style.backgroundImage || '');
      if (!m || !m[1] || (el as any).__raksmeGuard === m[1]) { return; }
      (el as any).__raksmeGuard = m[1];
      const probe = new Image();
      probe.onerror = () => el.classList.add('raksme-bg-missing');
      probe.onload = () => el.classList.remove('raksme-bg-missing');
      probe.src = m[1];
    });
  }

  /**
   * Section motion, the site's one standard enter/exit: each section fades in and
   * rises 24px as it scrolls into view, and reverses as it leaves (raksme-sections.css).
   * The hidden start state only applies once the host has .motion-ready, so sections
   * are never left invisible when IntersectionObserver is missing or motion is reduced.
   */
  private observeSections(): void {
    if (typeof window === 'undefined') { return; }
    const root: HTMLElement = this.host.nativeElement;
    const sections: Element[] = Array.prototype.slice.call(root.querySelectorAll('.raksme-page-section'));
    const reduce = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    if (reduce || !('IntersectionObserver' in window)) {
      root.classList.remove('motion-ready');
      return;
    }
    if (!this.observer) {
      this.observer = new (window as any).IntersectionObserver((entries: any[]) => {
        entries.forEach((e: any) => e.target.classList.toggle('is-inview', e.isIntersecting));
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    }
    root.classList.add('motion-ready');
    sections.forEach((el: Element) => this.observer.observe(el));
  }
}
